import { persist } from 'zustand/middleware';
import { createContext, useContext, useRef } from 'react';
import { createPanelInstanceSlice } from '@store/Quote/Panels';
import { createStructuredQuoteSlice } from '@store/Quote/StructuredQuote';
import { createUnsavedChangesSlice } from '@store/Quote/UnsavedChanges';
import { createLayoutSlice } from '@store/types/LayoutSlice.type';

import { shallow } from 'zustand/shallow';
import { TQuoteStore } from '@store/types/GlobalStore.type';
import { createStore } from 'zustand';
import { useStoreWithEqualityFn } from 'zustand/traditional';

const persistedFields = ['attributesById'];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createQuoteStore = (initProps: { quoteId: string }) => {
  return createStore<TQuoteStore>()(
    persist(
      (...args) => ({
        ...createPanelInstanceSlice(...args),
        ...createStructuredQuoteSlice(...args),
        ...createUnsavedChangesSlice(...args),
        ...createLayoutSlice(...args),
      }),
      {
        // FUTURE: to solve when we do split view (handle cleanup in some way so we don't have a zillion cached empty quote stores)
        // name: `lio-quote-storage-${initProps.quoteId}`,
        name: `lio-quote-storage`,
        version: 1.2,
        partialize: (state) =>
          Object.fromEntries(Object.entries(state).filter(([key]) => persistedFields.includes(key))),
      },
    ),
  );
};

export function QuoteWithStore({
  quoteId,
  defaultValues,
  children,
}: {
  quoteId: string;
  defaultValues?: Partial<ReturnType<typeof import('@store/Quote/QuoteStore').createQuoteStore>['getState']>;
  children: React.ReactNode;
}) {
  const storeRef = useRef<TQuoteStoreContext>(null);
  if (!storeRef.current) {
    storeRef.current = createQuoteStore({
      quoteId,
    });

    storeRef.current.setState((state) => ({
      ...state,
      ...defaultValues,
    }));
  }

  return <QuoteStoreContext.Provider value={storeRef.current}>{children}</QuoteStoreContext.Provider>;
}
export const useQuoteStore = <T,>(selector: (store: TQuoteStore) => T, equalityFn?: (a: any, b: any) => boolean): T => {
  const tableStoreContext = useContext(QuoteStoreContext);

  if (!tableStoreContext) {
    throw new Error(`useQuoteStore must be used within QuoteWithStore`);
  }

  return useStoreWithEqualityFn(tableStoreContext, selector, equalityFn ?? shallow);
};

export type TQuoteStoreContext = ReturnType<typeof createQuoteStore>;

export const QuoteStoreContext = createContext<TQuoteStoreContext | undefined>(undefined);

export function useCurrentQuoteStoreState() {
  const store = useContext(QuoteStoreContext);
  if (!store) {
    throw new Error('useCurrentQuoteStoreState must be used within QuoteWithStore');
  }
  return store.getState();
}
